import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Home,
  User,
  LogOut,
  Bell,
  Settings,
  ChevronRight,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import { VStack, HStack, Grid } from '@/components/core';

export function AdminHeader() {
  const { t } = useTranslation('admin');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount, notifications, markAllAsRead } = useNotifications();
  const [isAdminBellOpen, setIsAdminBellOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Click-Outside Handler für Notification-Dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsAdminBellOpen(false);
      }
    };

    if (isAdminBellOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdminBellOpen]);

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];

    // Add home
    breadcrumbs.push({ label: t('navigation.admin'), path: '/admin' });

    // Add path segments
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      if (segment === 'admin') return; // Skip admin segment

      currentPath += `/${segment}`;
      let label = segment.charAt(0).toUpperCase() + segment.slice(1);

      // Map specific segments to better labels
      const labelMap: Record<string, string> = {
        'dashboard': t('navigation.dashboard'),
        'users': t('navigation.users'),
        'characters': t('navigation.characters'),
        'settings': t('navigation.settings'),
        'character-behavior': t('navigation.characterBehavior'),
        'ai-settings': t('navigation.aiConfig'),
        'pricing': t('navigation.pricing'),
        'coin-packages': t('navigation.coinPackages'),
        'subscriptions': t('navigation.subscriptions'),
        'affiliates': t('navigation.affiliate'),
        'attributes': t('navigation.attributes'),
        'swipe': t('navigation.swipe'),
        'analytics': t('navigation.analytics'),
        'packages': t('navigation.packages'),
        'audit-logs': t('navigation.auditLogs'),
        'profile-visits': t('navigation.profileVisits'),
        'daily-challenges': t('navigation.dailyChallenges'),
        'character-locations': t('navigation.locations'),
        'billing': t('navigation.billingOverview'),
        'billing-plans': t('navigation.subscriptionPlansEditor'),
        'gifts': t('navigation.gifts'),
        'achievements': t('navigation.achievements'),
        'moderation': t('navigation.moderation'),
        'memories': t('navigation.memories'),
        'email-settings': t('navigation.emailConfiguration'),
        'language-settings': t('navigation.languageSettings'),
        'legal': t('navigation.legal'),
        'notification-settings': t('navigation.notificationSettings'),
        'creator-space': t('navigation.content'),
        'support': t('navigation.support'),
        'partner-billing': t('navigation.partnerBilling'),
        'revenue': t('navigation.revenue'),
        'ai-costs': t('navigation.aiCosts'),
        'ai-fallback': t('navigation.aiFallback'),
        'data-transfer': t('navigation.exportImport'),
        'platform-tariff': t('navigation.platformTariff'),
        'user-chats': t('navigation.chatsReadOnly'),
        'ai-character-bulk-creation': t('navigation.aiBulkCreation'),
        'peak-and-badge': t('navigation.peakAndBadge')
      };

      label = labelMap[segment] || label;
      breadcrumbs.push({
        label,
        path: `/admin${currentPath}`,
        isLast: index === pathSegments.length - 2 // -2 because we skip 'admin'
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <header className="hidden md:block fixed top-16 left-0 right-0 z-40 design-glass-desktop-nav">
      <HStack align="center" justify="between" className="h-20 px-token-md lg:px-token-lg">
        {/* Left Section - Logo & Breadcrumbs */}
        <HStack gap="md" align="center" className="lg:gap-token-md min-w-0 flex-nowrap">
          {/* Logo */}
          <HStack as={Link} to="/admin" gap="sm" align="center" className="flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-token-button flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg lg:text-xl font-bold design-text-primary hidden lg:block">
              {t('header.adminPanel')}
            </span>
          </HStack>

          {/* Breadcrumbs - auf md sichtbar, bei wenig Platz gekürzt */}
          <HStack as="nav" gap="sm" align="center" className="lg:gap-token-sm min-w-0 overflow-hidden flex-nowrap">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              // Auf kleineren Screens nur letzte 2 Breadcrumbs zeigen
              const shouldHide = breadcrumbs.length > 2 && index < breadcrumbs.length - 2;

              return (
                <React.Fragment key={crumb.path}>
                  {index > 0 && (
                    <ChevronRight className={cn(
                      "h-4 w-4 design-text-secondary flex-shrink-0",
                      shouldHide && "hidden lg:block"
                    )} />
                  )}
                  {isLast ? (
                    <span className={cn(
                      "design-text-primary font-medium truncate max-w-[120px] lg:max-w-[200px]",
                      shouldHide && "hidden lg:inline"
                    )}>
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.path}
                      className={cn(
                        "design-text-secondary hover:design-text-primary transition-colors truncate max-w-[100px] lg:max-w-[150px]",
                        shouldHide && "hidden lg:inline"
                      )}
                    >
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </HStack>
        </HStack>

        {/* Right Section - Actions & User */}
        <HStack gap="md" align="center" className="lg:gap-token-md flex-shrink-0">
          {/* Home Link - immer sichtbar */}
          <Link to="/">
            <Button variant="ghost" size="sm" className="design-text-secondary hover:design-text-primary px-token-sm lg:px-token-sm">
              <Home className="h-4 w-4" />
              <span className="hidden lg:inline ml-token-sm">{t('header.toSite')}</span>
            </Button>
          </Link>

          {/* Notification Bell */}
          <div className="relative" ref={dropdownRef}>
            <Button
              ref={buttonRef}
              variant="ghost"
              size="icon"
              className="design-text-secondary hover:design-text-primary relative h-9 w-9"
              onClick={() => setIsAdminBellOpen(!isAdminBellOpen)}
              aria-label={t('header.adminNotifications')}
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-token-xs rounded-full text-[10px] leading-4 font-bold text-center bg-error text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            <div
              className={cn(
                "absolute right-0 top-full mt-token-sm w-80 lg:w-96 rounded-token-card shadow-token-modal",
                "max-h-[500px] overflow-hidden flex flex-col",
                "glass-dropdown-panel",
                "opacity-0 invisible pointer-events-none transition-all duration-200 z-[100]",
                isAdminBellOpen && "opacity-100 visible pointer-events-auto"
              )}
            >
              <div className="p-token-sm lg:p-token-md border-b border-white/10">
                <HStack gap="md" align="center" className="justify-between">
                  <h3 className="text-base lg:text-lg font-semibold design-text-primary truncate">{t('header.adminNotifications')}</h3>
                  <HStack gap="sm" align="center">
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => { await markAllAsRead(); }}
                        className="text-xs design-text-secondary hover:design-text-primary h-8 px-token-sm"
                      >
                        <Check size={14} className="mr-token-xs" />
                        <span className="hidden lg:inline">{t('header.markAllRead')}</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setIsAdminBellOpen(false); navigate('/admin/notification-settings'); }}
                      className="h-8 w-8 design-text-secondary hover:design-text-primary"
                    >
                      <Settings size={16} />
                    </Button>
                  </HStack>
                </HStack>
              </div>

              <div className="flex-1 overflow-y-auto">
                {notifications.filter(n => !n.is_read).slice(0, 5).length === 0 ? (
                  <div className="p-token-lg lg:p-token-xl text-center">
                    <Bell className="mx-auto h-10 w-10 lg:h-12 lg:w-12 text-white/20 mb-token-sm" />
                    <p className="design-text-secondary text-sm">{t('header.noNotifications')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {notifications
                      .filter(n => !n.is_read)
                      .slice(0, 5)
                      .map((notification) => (
                        <div
                          key={notification.id}
                          className="hover:bg-white/5 transition-colors cursor-pointer p-token-sm text-sm design-text-secondary"
                          onClick={() => setIsAdminBellOpen(false)}
                        >
                          {notification.title || t('common:notifications.info')}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="p-token-sm lg:p-token-sm border-t border-white/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIsAdminBellOpen(false); navigate('/admin/notification-settings'); }}
                  className="w-full text-xs design-text-accent hover:design-text-primary justify-center"
                >
                  {t('header.viewAll')}
                  <ChevronRight size={14} className="ml-token-xs" />
                </Button>
              </div>
            </div>
          </div>

          {/* User Menu */}
          <HStack gap="md" align="center" className="lg:gap-token-sm border-l border-white/10 pl-token-sm lg:pl-token-md">
            <div className="hidden lg:block text-right">
              <p className="design-text-primary text-sm font-medium truncate max-w-[120px]">{user?.username}</p>
              <p className="design-text-secondary text-xs">
                {user?.role === 'root_admin' ? 'Root-Admin' : user?.role === 'demo_admin' ? 'Demo-Admin' : 'Admin'}
              </p>
            </div>

            <HStack gap="sm" align="center">
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="design-text-secondary hover:text-error h-9 w-9"
                title={t('header.logout', { defaultValue: 'Abmelden' })}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </HStack>
          </HStack>
        </HStack>
      </HStack>
    </header>
  );
}